import React, { useState, useMemo } from "react";
import { formatAccountName } from "../../../../utils/formatters";
import { isBankLinkedToAccount } from "../../../../utils/bankAccounts";

const UnlinkedBankAccountsForm = ({
  householdData,
  fieldData,
  missingFields,
  entityId,
  onSave,
  onSaveAndExit,
  saving,
  nextLabel,
}) => {
  const accounts = useMemo(() => householdData?.accounts || [], [householdData]);
  const members = useMemo(() => householdData?.members || [], [householdData]);
  const bankAccounts = useMemo(() => householdData?.bank_accounts || [], [householdData]);

  const unmappedBankAccounts = useMemo(() => {
    if (!bankAccounts.length || !accounts.length) return [];
    return bankAccounts.filter((ba) => {
      const isLinked = accounts.some((account) => {
        const inLinks = isBankLinkedToAccount(account, ba.id);
        const inWithdrawals = account.withdrawals?.some(
          (w) => w.destination_identifier === ba.id,
        );
        return inLinks || inWithdrawals;
      });
      return !isLinked;
    });
  }, [bankAccounts, accounts]);

  // All brokerage accounts are valid link targets now that multiple banks are supported.
  const availableBrokerageAccounts = useMemo(() => accounts, [accounts]);

  // State: { [bankAccountId]: [selectedBrokerageAccountId, ...] }
  const [selections, setSelections] = useState(() => {
    const initial = {};
    unmappedBankAccounts.forEach((ba) => {
      initial[ba.id] = [];
    });
    return initial;
  });

  const handleToggle = (bankAccountId, brokerageAccountId) => {
    setSelections((prev) => {
      const current = prev[bankAccountId] || [];
      const updated = current.includes(brokerageAccountId)
        ? current.filter((id) => id !== brokerageAccountId)
        : [...current, brokerageAccountId];
      return { ...prev, [bankAccountId]: updated };
    });
  };

  const buildPayload = () => {
    // Aggregate link ops per brokerage account — multiple banks may target the same account.
    const accountsPayload = {};
    Object.entries(selections).forEach(([bankAccountId, brokerageIds]) => {
      brokerageIds.forEach((brokerageId) => {
        if (!accountsPayload[brokerageId]) {
          accountsPayload[brokerageId] = { bank_accounts: { link: [] } };
        }
        accountsPayload[brokerageId].bank_accounts.link.push(bankAccountId);
      });
    });
    return { accounts: accountsPayload };
  };

  const hasSelections = Object.values(selections).some((arr) => arr.length > 0);

  const handleSubmit = (cb) => {
    if (!hasSelections) {
      // Nothing selected — still call save to advance
      cb({});
      return;
    }
    cb(buildPayload());
  };

  if (unmappedBankAccounts.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <p className="text-sm">All bank accounts have been linked.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <span className="text-sm text-amber-600 font-medium">
          {unmappedBankAccounts.length} bank account{unmappedBankAccounts.length !== 1 ? "s" : ""} not linked to any brokerage account
        </span>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Select the brokerage accounts you want to link each bank account to.
      </p>

      {unmappedBankAccounts.map((ba) => {
        const selected = selections[ba.id] || [];
        return (
          <div
            key={ba.id}
            className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm mb-4"
          >
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex justify-between items-center">
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <span className="bg-amber-100 text-amber-700 w-6 h-6 rounded-full flex items-center justify-center text-xs">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </span>
                {ba.bank_name || "Bank"} — {ba.account_number || "N/A"}
              </h3>
              {selected.length > 0 && (
                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold uppercase tracking-wider border border-emerald-100">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  {selected.length} linked
                </span>
              )}
            </div>

            <div className="p-4">
              {availableBrokerageAccounts.length === 0 ? (
                <p className="text-sm text-gray-500 italic">
                  No brokerage accounts available for linking.
                </p>
              ) : (
                <div className="space-y-2">
                  {availableBrokerageAccounts.map((acc, idx) => {
                    const isChecked = selected.includes(acc.id);
                    const accountName = formatAccountName(acc, idx + 1, members);
                    return (
                      <label
                        key={acc.id}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                          isChecked
                            ? "bg-emerald-50 border-emerald-300"
                            : "bg-white border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleToggle(ba.id, acc.id)}
                          className="w-4 h-4 text-emerald-500 border-gray-300 rounded focus:ring-emerald-500"
                        />
                        <span className="text-sm text-gray-800 font-medium">
                          {accountName}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <button
          onClick={() => handleSubmit(onSaveAndExit)}
          disabled={saving}
          className="px-6 py-2.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Save & Exit
        </button>
        <button
          onClick={() => handleSubmit(onSave)}
          disabled={saving}
          className="px-6 py-2.5 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {saving && (
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
          {nextLabel || "Save & Next \u2192"}
        </button>
      </div>
    </div>
  );
};

export default UnlinkedBankAccountsForm;
